import {
  KdsButton,
  KdsTagGroup,
  type KdsTagProps,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiAddFill, RiCloseFill, RiResetLeftLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { sortBy } from "lodash";
import { useFormContext } from "react-hook-form";
import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";

import { OrderItemStatusEnum } from "@/config/apiClient.types";
import { injectDataCy } from "@/helpers/injectDataCy";
import { type InferredOrderDetailsAddQuoteFormPageSchema } from "@/pages/OrderDetailsAddQuoteFormPage/OrderDetailsAddQuoteFormPage.schema";
import { type GetOrderItemStepProps } from "../ModalAddQuote.types";

export const AddQuoteOrderItemStep = ({
  orderItems,
  currentSandboxItemId,
  isFromAws,
}: GetOrderItemStepProps) => {
  const { t } = useTranslate();
  // Get form
  const methods = useFormContext<InferredOrderDetailsAddQuoteFormPageSchema>();

  // Get Selected SandboxItems
  const selectedSandboxItems = methods.watch("sandboxItemIds");

  const isDirty = selectedSandboxItems.length > 1;

  // Handle Add All Products
  const handleAddAllProducts = () => {
    methods.setValue("sandboxItemIds", [
      currentSandboxItemId,
      ...orderItems
        .filter(
          (oi) =>
            oi.sandboxItemId !== currentSandboxItemId &&
            oi.status !== OrderItemStatusEnum.CANCELLED &&
            oi.Workflow.PostponedActivities.some(
              (postponedActivity) =>
                postponedActivity.activity ===
                  WorkflowPostponedActivityReadDtoActivityEnum.AddQuote ||
                postponedActivity.activity ===
                  WorkflowPostponedActivityReadDtoActivityEnum.SendQuote
            )
        )
        .map((oi) => oi.sandboxItemId),
    ]);
  };

  // Handle Reset
  const handleReset = () => {
    methods.setValue("sandboxItemIds", [currentSandboxItemId]);
  };

  // Handle Add Product
  const handleAddProduct = (sandboxItemId: string) => {
    const currentIds = methods.getValues("sandboxItemIds");
    if (!methods.getValues("sandboxItemIds").includes(sandboxItemId)) {
      methods.setValue("sandboxItemIds", [...currentIds, sandboxItemId]);
    }
  };

  // Handle Remove Product
  const handleRemoveProduct = (sandboxItemId: string) => {
    const currentIds = methods.getValues("sandboxItemIds");
    methods.setValue(
      "sandboxItemIds",
      currentIds.filter((id) => id !== sandboxItemId)
    );
  };
  return (
    <div className="flex w-full flex-col gap-6 p-6 px-8">
      {!isFromAws && (
        <>
          <div className="flex items-start justify-between gap-4">
            <KdsText size="subtitle-md">
              {t("oms.addQuote.stepper.orderItemsStepSubtitle")}
            </KdsText>

            <div className="flex items-start justify-between gap-4">
              <KdsButton
                variant="outlined"
                color="neutral"
                iconLeft={RiAddFill}
                onPress={handleAddAllProducts}
                {...injectDataCy("addAllOrderItem")}
              >
                {t("buttons.allProducts")}
              </KdsButton>

              <KdsButton
                disabled={!isDirty}
                iconLeft={RiResetLeftLine}
                onPress={handleReset}
              >
                {t("buttons.reset")}
              </KdsButton>
            </div>
          </div>

          <div className="flex min-h-8 flex-wrap items-center gap-2">
            <KdsTagGroup
              tagsProps={sortBy(orderItems, "sandboxItem.name")
                ?.filter(
                  (oi) =>
                    !selectedSandboxItems.includes(oi.sandboxItemId) &&
                    oi.sandboxItemId !== currentSandboxItemId &&
                    oi.status !== OrderItemStatusEnum.CANCELLED &&
                    oi.Workflow.PostponedActivities.some(
                      (postponedActivity) =>
                        postponedActivity.activity ===
                          WorkflowPostponedActivityReadDtoActivityEnum.AddQuote ||
                        postponedActivity.activity ===
                          WorkflowPostponedActivityReadDtoActivityEnum.SendQuote
                    )
                  // Replace with that filter if we decide to also show disabled orderItems -> !selectedSandboxItems.includes(oi.sandboxItemId)
                )
                .map((oi) => ({
                  key: oi.sandboxItem.id,
                  children: oi.sandboxItem.name,
                  disabled:
                    !!oi.sandboxItemQuoteId ||
                    oi.status === OrderItemStatusEnum.CANCELLED ||
                    oi.sandboxItem.SandboxItemQuote.some((q) => !!q.sentAt),
                  iconRight: RiAddFill,
                  action: () => handleAddProduct(oi.sandboxItemId),
                  size: "md",
                  color: "neutral",
                }))}
            />
          </div>

          <div className="w-full border-t border-slate-200" />
        </>
      )}

      <KdsText size="subtitle-md">
        {t("oms.addQuote.stepper.orderItemStepListTitle")}
      </KdsText>

      <div className="flex min-h-28 flex-wrap items-start gap-2 rounded-xl border border-slate-200 p-4">
        <KdsTagGroup
          tagsProps={selectedSandboxItems
            .map((id): KdsTagProps | null => {
              const orderItem = orderItems?.find(
                (oi) => oi.sandboxItemId === id
              );
              if (!orderItem) {
                return null;
              }

              const isFirstTag = id === currentSandboxItemId || isFromAws;

              return {
                children: orderItem.sandboxItem.name,
                ...(!isFirstTag && {
                  action: () => handleRemoveProduct(orderItem.sandboxItemId),
                }),
                iconRight: isFirstTag ? RiCloseFill : undefined,
                state: isFirstTag ? "neutral" : "secondary",
                variant: isFirstTag ? "outlined" : "alpha",
                size: "md",
              };
            })
            .filter((tag): tag is KdsTagProps => tag !== null)}
        />
      </div>
    </div>
  );
};
